import React from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRegEnvelope, FaWhatsapp, FaInstagram, FaTiktok } from 'react-icons/fa';
import { IoLogoFacebook } from 'react-icons/io';
import { LuPhone } from 'react-icons/lu';
import { SlLocationPin } from 'react-icons/sl';
import logo from '../assets/Navbar/aa5f6aee-1feb-4bdf-89d3-9f4ed840ea5d.png';

// Animation variants
const textVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

const Footer = () => {
    // Important: use the 'footer' namespace
    const { t } = useTranslation('footer');

    const currentYear = new Date().getFullYear();

    const navigationLinks = [
        { link: '/home', key: 'navHome' },
        { link: '/about-us', key: 'navAbout' },
        { link: '/contact-us', key: 'navContact' },
        { link: '/rooms', key: 'navRooms' },
        { link: '/ayurveda', key: 'navAyurveda' },
    ];

    return (
        <div className="relative bg-[#173F2D]">
            {/* Main Footer Content */}
            <motion.footer
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={textVariants}
                className="relative z-10 px-4 pt-4 sm:px-5"
                style={{ contentVisibility: 'auto' }}
            >
                <div className="h-auto py-8 sm:py-10">
                    <div className="mx-auto max-w-7xl gap-8 lg:flex lg:items-start lg:justify-between lg:gap-6">
                        <div className="w-full lg:w-[32%]">
                            <div className="mx-auto w-full max-w-[180px] lg:mx-0 lg:max-w-[150px]">
                                <div className="flex justify-center lg:justify-start">
                                    <img
                                        src={logo}
                                        alt="Mawathage Services Logo"
                                        width={100}
                                        height={80}
                                        className="rounded-lg object-cover"
                                        loading="eager"
                                        decoding="async"
                                        fetchPriority="high"
                                    />
                                </div>
                                <div className="my-5 flex justify-center gap-4 sm:gap-5 lg:justify-start">
                                    <a
                                        href="https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our Facebook page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <IoLogoFacebook size={20} className="text-[#F2D36B]" />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our Instagram page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaInstagram size={20} className="text-[#F2D36B]" />
                                    </a>
                                    <a
                                        href="https://wa.me/94742742794"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Contact us on WhatsApp"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaWhatsapp size={20} className="text-[#F2D36B]" />
                                    </a>
                                    <a
                                        href="https://www.tiktok.com/@.mahi.beach.ayurv?_r=1&_t=ZS-94BSC88Jb2S"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our TikTok page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaTiktok size={20} className="text-[#F2D36B]" />
                                    </a>
                                    <a
                                        href="mailto:info@mawatage-services.com"
                                        aria-label="Email us"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaRegEnvelope size={20} className="text-[#F2D36B]" />
                                    </a>
                                </div>
                                <div className="text-center text-[14px] leading-[22px] text-white/80 sm:text-[16px] sm:leading-[25px] lg:text-left">
                                    {t('description')}
                                </div>
                            </div>
                        </div>

                        <div className="my-8 w-full lg:my-0 lg:w-[22%] lg:pl-4">
                            <div className="text-center text-[16px] font-medium text-[#F2D36B] sm:text-[18px] lg:text-left">
                                {t('linksTitle')}
                            </div>
                            <div className="mt-5 flex flex-col items-center gap-2 leading-8 lg:items-start">
                                {navigationLinks.map((item, index) => (
                                    <NavLink
                                        key={index}
                                        to={item.link}
                                        className="w-full max-w-[180px] text-center text-[14px] font-medium text-white/80 transition-colors duration-200 hover:text-[#F2D36B] sm:text-[16px] lg:max-w-none lg:text-left"
                                        style={({ isActive }) => ({
                                            textDecoration: isActive ? 'underline' : 'none',
                                            textUnderlineOffset: '4px',
                                        })}
                                    >
                                        {t(item.key)}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        <div className="flex w-full flex-col items-center text-center lg:w-[42%] lg:items-start lg:pl-4 lg:text-left">
                            <div className="text-[16px] font-medium text-[#F2D36B] sm:text-[18px]">
                                {t('contactTitle')}
                            </div>
                            <div className="mt-5 space-y-3 leading-7 sm:space-y-4">
                                <div className="flex items-start justify-center gap-3 lg:justify-start">
                                    <LuPhone size={18} className="mt-1 shrink-0 text-[#F2D36B]" />
                                    <a
                                        href="tel:+94742742794"
                                        className="text-[14px] font-medium text-white/85 transition-colors duration-200 hover:text-[#F2D36B] sm:text-[16px]"
                                    >
                                        +94 74 274 2794 <span className="text-sm text-white/60">- Gayan Mawatage</span>
                                    </a>
                                </div>
                                <div className="flex items-start justify-center gap-3 lg:justify-start">
                                    <LuPhone size={18} className="mt-1 shrink-0 text-[#F2D36B]" />
                                    <a
                                        href="tel:+94762236647"
                                        className="text-[14px] font-medium text-white/85 transition-colors duration-200 hover:text-[#F2D36B] sm:text-[16px]"
                                    >
                                        +94 76 223 6647 <span className="text-sm text-white/60">- Carola</span>
                                    </a>
                                </div>
                                <div className="flex items-start justify-center gap-3 lg:justify-start">
                                    <FaRegEnvelope size={18} className="mt-1 shrink-0 text-[#F2D36B]" />
                                    <a
                                        href="mailto:info@mawatage-services.com"
                                        className="break-all text-[14px] font-medium text-white/85 transition-colors duration-200 hover:text-[#F2D36B] sm:text-[16px]"
                                    >
                                        info@mawatage-services.com
                                    </a>
                                </div>
                                <div className="flex items-start justify-center gap-3 lg:justify-start">
                                    <SlLocationPin size={18} className="mt-1 shrink-0 text-[#F2D36B]" />
                                    <a
                                        href="https://maps.app.goo.gl/338Jvrk1ux6z3RKPA"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-left text-[14px] font-medium text-white/85 transition-colors duration-200 hover:text-[#F2D36B] sm:text-[16px]"
                                    >
                                        No. 61, Police Bungalow Road, Moragalla, Beruwala
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.footer>

            {/* Bottom Bar: Copyright & Developer */}
            <div className="relative z-10 flex min-h-[70px] flex-col items-center justify-center gap-1 border-t border-[#C9A227]/35 bg-[#102E21] px-4 py-3 text-white sm:min-h-[50px] sm:py-2">
                <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="text-center text-[12px] font-medium text-white sm:text-[14px]"
                >
                    {t('copyright', { year: currentYear })}
                </motion.span>
                <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="text-center text-[12px] font-medium text-white sm:text-[14px]"
                >
                    {t('developedBy')}{' '}
                    <a
                        href="tel:+94764608225"
                        className="text-white transition-colors duration-200 hover:text-[#F2D36B]"
                    >
                        {t('developerName')}
                    </a>
                </motion.span>
            </div>
        </div>
    );
};

export default Footer;